import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { LibraryEditor } from "./use-library-editor"

import { type PreflightReport, runLibraryPreflightCheck } from "@/api/admin-api"
import { AlertTriangle, CheckCircle2, HardDrive, Key, XCircle } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"

import { EditorSectionCard, FieldRow } from "./editor-fields"
import { isSharePointFamily } from "./library-editor-model"
import { MASKED_SECRET_INDICATOR, readSpoField } from "../library-auth-utils"
import { LibraryCopyAuthSelect } from "../library-copy-auth-select"
import { SharePointFileBrowser, SharePointVerifyConnection } from "../sharepoint-connection-verify"

type ConnectionSectionProps = {
  editor: LibraryEditor
}

export function ConnectionSection({ editor }: ConnectionSectionProps) {
  const { t } = useTranslation()
  const { values, mode, verify } = editor
  const auth = values.source_config.auth
  const isSharePoint = isSharePointFamily(values.source_type)

  let badge: string | null = null
  if (verify.allStepsOk) badge = t("adminDashboard.libraries.editor.verified", "Verified")
  else if (editor.authConfigured)
    badge = t("adminDashboard.libraries.editor.configured", "Configured")

  return (
    <EditorSectionCard
      icon={<Key className="size-4" />}
      id="library-connection"
      open={editor.openSections.connection}
      title={t("adminDashboard.libraries.editor.sections.connection", "Connection")}
      badge={
        badge ? (
          <Badge className="text-xs" variant={verify.allStepsOk ? "default" : "outline"}>
            {badge}
          </Badge>
        ) : undefined
      }
      description={t(
        "adminDashboard.libraries.editor.connectionHelp",
        "App registration used to read the source. Leave empty to use the service's own identity."
      )}
      onOpenChange={(open) => editor.setSectionOpen("connection", open)}
    >
      {editor.isCopyAuthType && editor.copyAuthCandidates.length > 0 && (
        <LibraryCopyAuthSelect
          candidates={editor.copyAuthCandidates}
          value={editor.copyAuthId}
          currentHostname={
            typeof values.source_config.hostname === "string"
              ? values.source_config.hostname
              : undefined
          }
          onChange={editor.handleCopyAuthChange}
        />
      )}

      <FieldRow id="client_id" label={t("adminDashboard.libraries.editor.clientId", "Client ID")}>
        <Input
          id="client_id"
          placeholder="Application (client) ID"
          value={auth?.client_id ?? ""}
          onChange={(event) => editor.updateAuth("client_id", event.target.value)}
        />
      </FieldRow>
      <FieldRow id="tenant_id" label={t("adminDashboard.libraries.editor.tenantId", "Tenant ID")}>
        <Input
          id="tenant_id"
          placeholder="Directory (tenant) ID"
          value={auth?.tenant_id ?? ""}
          onChange={(event) => editor.updateAuth("tenant_id", event.target.value)}
        />
      </FieldRow>
      <FieldRow
        id="client_secret"
        help={t(
          "adminDashboard.libraries.editor.clientSecretHelp",
          "Name of the secret in Azure Key Vault. The secret value itself is never stored."
        )}
        label={t(
          "adminDashboard.libraries.editor.clientSecretRef",
          "Client secret (Key Vault reference)"
        )}
      >
        <Input
          id="client_secret"
          placeholder={editor.secretPlaceholder}
          value={editor.rawSecret === MASKED_SECRET_INDICATOR ? "" : editor.rawSecret}
          onChange={(event) => editor.updateAuth("client_secret", event.target.value)}
        />
      </FieldRow>

      {isSharePoint && (
        <>
          <SharePointVerifyConnection
            canVerify={editor.canVerify}
            verify={verify}
            onVerify={editor.handleVerify}
          />
          {mode === "create" && !editor.authConfigured && <PreflightScan editor={editor} />}
          <SharePointFileBrowser verify={verify} onLoadMore={editor.handleLoadMore} />
        </>
      )}
    </EditorSectionCard>
  )
}

/** Service-identity scan of the target (create only, when no app registration is configured). */
function PreflightScan({ editor }: { editor: LibraryEditor }) {
  const { t } = useTranslation()
  const [isScanning, setIsScanning] = useState(false)
  const [report, setReport] = useState<PreflightReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  const config = editor.values.source_config
  const hostname = typeof config.hostname === "string" ? config.hostname : ""
  const siteName = readSpoField(config, "siteName")
  if (!hostname || !siteName) return null

  const run = async () => {
    setIsScanning(true)
    setError(null)
    setReport(null)
    try {
      setReport(
        await runLibraryPreflightCheck({
          sharepoint: {
            hostname,
            siteName,
            listName: readSpoField(config, "listName") || undefined,
            folderPath: readSpoField(config, "folderPath") || undefined,
          },
        })
      )
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to scan library")
    } finally {
      setIsScanning(false)
    }
  }

  const unsupported = report?.warnings.filter((w) => w.type === "unsupported_extension").length ?? 0
  const oversized = report?.warnings.filter((w) => w.type === "oversized_file").length ?? 0
  const typeEntries = report
    ? Object.entries(report.stats.type_distribution).sort(([, a], [, b]) => b - a)
    : []

  return (
    <div className="space-y-2">
      <Button
        className="w-full"
        disabled={isScanning}
        size="sm"
        type="button"
        variant="outline"
        onClick={() => void run()}
      >
        {isScanning ? (
          <>
            <Spinner className="mr-1 size-4" />
            {t("adminDashboard.libraries.editor.scanning", "Scanning...")}
          </>
        ) : (
          t("adminDashboard.libraries.editor.scanLibrary", "Scan with service identity")
        )}
      </Button>
      <p className="text-muted-foreground text-center text-xs">
        {t(
          "adminDashboard.libraries.editor.scanHelp",
          "Preview file counts using the service's own credentials."
        )}
      </p>
      {error && (
        <Alert variant="destructive">
          <XCircle className="size-4" />
          <AlertTitle>{t("adminDashboard.libraries.editor.scanError", "Scan failed")}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {report && (
        <div className="space-y-3 rounded-md border p-3">
          <div className="flex items-center gap-2">
            {report.warnings.length === 0 ? (
              <CheckCircle2 className="text-success size-5" />
            ) : (
              <AlertTriangle className="text-warning size-5" />
            )}
            <span className="font-medium">
              {t("adminDashboard.libraries.editor.scanResults", "Scan results")}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="flex items-center gap-2">
              <HardDrive className="text-muted-foreground size-4" />
              {t("adminDashboard.libraries.editor.totalFiles", "Total files:")}{" "}
              <strong>{report.stats.total_count}</strong>
            </span>
            <span>
              {t("adminDashboard.libraries.editor.totalSize", "Total size:")}{" "}
              <strong>{report.stats.total_size_formatted}</strong>
            </span>
          </div>
          {typeEntries.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {typeEntries.slice(0, 8).map(([ext, count]) => (
                <Badge key={ext} className="text-xs" variant="secondary">
                  {ext}: {count}
                </Badge>
              ))}
              {typeEntries.length > 8 && (
                <Badge className="text-xs" variant="outline">
                  +{typeEntries.length - 8}
                </Badge>
              )}
            </div>
          )}
          {(unsupported > 0 || oversized > 0) && (
            <div className="text-muted-foreground space-y-0.5 text-xs">
              {unsupported > 0 && (
                <p>
                  {unsupported}{" "}
                  {t(
                    "adminDashboard.libraries.editor.unsupportedFiles",
                    "file(s) with unsupported extensions will be skipped"
                  )}
                </p>
              )}
              {oversized > 0 && (
                <p>
                  {oversized}{" "}
                  {t(
                    "adminDashboard.libraries.editor.oversizedFiles",
                    "file(s) exceed 100 MB and may cause processing issues"
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
